import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'


import { get, post, patch, del } from '@/lib/api'

import type { DeveloperGroup, IssueType, IssueStatus, IssuePriority } from '@company/shared'

// ==================== Types ====================

export type { IssueType, IssueStatus, IssuePriority }

export interface Issue {
  id: string
  projectId: string
  code: string
  title: string
  description: string | null
  type: IssueType
  status: IssueStatus
  priority: IssuePriority
  assigneeId: string | null
  reporterId: string
  milestoneId: string | null
  parentId: string | null
  developerGroup: DeveloperGroup | null
  estimatedHours: number | null
  actualHours: number | null
  startDate: string | null
  dueDate: string | null
  completedAt: string | null
  blockedReason: string | null
  labels: string | null
  sortOrder: number | null
  createdAt: string
  updatedAt: string | null
  deletedAt: string | null
}

export interface IssueHistory {
  id: string
  issueId: string
  userId: string
  field: string
  oldValue: string | null
  newValue: string | null
  createdAt: string
}

export interface IssueCard {
  id: string
  code: string
  title: string
  type: IssueType
  priority: IssuePriority
  assigneeId: string | null
  assigneeName?: string | null
  participantCount?: number
  developerGroup: DeveloperGroup | null
  labels: string[]
  sortOrder: number
  dueDate: string | null
}

export interface KanbanColumn {
  status: IssueStatus
  issues: IssueCard[]
}

export interface KanbanData {
  columns: KanbanColumn[]
}

export interface IssueListParams {
  projectId?: string
  status?: IssueStatus | IssueStatus[]
  type?: IssueType | IssueType[]
  priority?: IssuePriority | IssuePriority[]
  assigneeId?: string
  reporterId?: string
  milestoneId?: string
  parentId?: string | null
  developerGroup?: DeveloperGroup
  search?: string
  page?: number
  pageSize?: number
  sortBy?: 'createdAt' | 'updatedAt' | 'priority' | 'dueDate' | 'sortOrder'
  sortOrder?: 'asc' | 'desc'
}

export interface KanbanFilters {
  assigneeId?: string
  developerGroup?: DeveloperGroup
  milestoneId?: string
  priority?: IssuePriority | IssuePriority[]
}

export interface CreateIssueInput {
  projectId: string
  title: string
  description?: string
  type?: IssueType
  priority?: IssuePriority
  assigneeId?: string
  /** 产品经理 ID（可选，默认为当前用户） */
  reporterId?: string
  milestoneId?: string
  parentId?: string
  estimatedHours?: number
  startDate?: string
  dueDate?: string
  labels?: string[]
}

export interface UpdateIssueInput {
  title?: string
  description?: string
  type?: IssueType
  priority?: IssuePriority
  assigneeId?: string | null
  milestoneId?: string | null
  parentId?: string | null
  estimatedHours?: number | null
  startDate?: string | null
  dueDate?: string | null
  labels?: string[]
  blockedReason?: string
}

export interface UpdateStatusInput {
  status: IssueStatus
  blockedReason?: string
}

// ==================== Query Keys ====================

export const issueKeys = {
  all: ['issues'] as const,
  lists: () => [...issueKeys.all, 'list'] as const,
  list: (params: IssueListParams) => [...issueKeys.lists(), params] as const,
  details: () => [...issueKeys.all, 'detail'] as const,
  detail: (id: string) => [...issueKeys.details(), id] as const,
  history: (id: string) => [...issueKeys.detail(id), 'history'] as const,
  children: (id: string) => [...issueKeys.detail(id), 'children'] as const,
  kanban: (projectId: string, filters: KanbanFilters) => [...issueKeys.all, 'kanban', projectId, filters] as const,
}

// ==================== Hooks ====================

/**
 * 获取任务列表
 */
export function useIssues(params: IssueListParams = {}) {
  return useQuery({
    queryKey: issueKeys.list(params),
    queryFn: () => get<Issue[]>('/issues', params as Record<string, unknown>),
  })
}

/**
 * 获取单个任务
 */
export function useIssue(id: string) {
  return useQuery({
    queryKey: issueKeys.detail(id),
    queryFn: () => get<Issue>(`/issues/${id}`),
    enabled: !!id,
  })
}

/**
 * 创建任务
 */
export function useCreateIssue() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateIssueInput) => post<Issue>('/issues', data),
    onSuccess: (issue) => {
      void queryClient.invalidateQueries({ queryKey: issueKeys.lists() })
      if (issue.projectId) {
        void queryClient.invalidateQueries({ queryKey: ['issues', 'kanban', issue.projectId] })
      }
    },
  })
}

/**
 * 更新任务
 */
export function useUpdateIssue() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateIssueInput }) =>
      patch<Issue>(`/issues/${id}`, data),
    onSuccess: (issue) => {
      void queryClient.invalidateQueries({ queryKey: issueKeys.detail(issue.id) })
      void queryClient.invalidateQueries({ queryKey: issueKeys.lists() })
      if (issue.projectId) {
        void queryClient.invalidateQueries({ queryKey: ['issues', 'kanban', issue.projectId] })
      }
    },
  })
}

/**
 * 删除任务
 */
export function useDeleteIssue() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => del<null>(`/issues/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: issueKeys.lists() })
      void queryClient.invalidateQueries({ queryKey: ['issues', 'kanban'] })
    },
  })
}

/**
 * 更新任务状态
 */
export function useUpdateIssueStatus() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateStatusInput }) =>
      patch<Issue>(`/issues/${id}/status`, data),
    onSuccess: (issue) => {
      void queryClient.invalidateQueries({ queryKey: issueKeys.detail(issue.id) })
      void queryClient.invalidateQueries({ queryKey: issueKeys.lists() })
      if (issue.projectId) {
        void queryClient.invalidateQueries({ queryKey: ['issues', 'kanban', issue.projectId] })
      }
    },
  })
}

/**
 * 获取任务历史
 */
export function useIssueHistory(issueId: string) {
  return useQuery({
    queryKey: issueKeys.history(issueId),
    queryFn: () => get<IssueHistory[]>(`/issues/${issueId}/history`),
    enabled: !!issueId,
  })
}

/**
 * 获取子任务
 */
export function useIssueChildren(issueId: string) {
  return useQuery({
    queryKey: issueKeys.children(issueId),
    queryFn: () => get<Issue[]>(`/issues/${issueId}/children`),
    enabled: !!issueId,
  })
}

/**
 * 获取看板数据
 */
export function useKanbanData(projectId: string, filters: KanbanFilters = {}) {
  return useQuery({
    queryKey: issueKeys.kanban(projectId, filters),
    queryFn: () => get<KanbanData>(`/issues/projects/${projectId}/kanban`, filters as Record<string, unknown>),
    enabled: !!projectId,
  })
}

/**
 * 更新任务排序
 */
export function useUpdateIssueSortOrder() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, sortOrder }: { id: string; sortOrder: number }) =>
      patch<null>(`/issues/${id}/sort`, { sortOrder }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['issues', 'kanban'] })
    },
  })
}

// ==================== Assignment Hooks (Deprecated in use-issues, use use-issue-assignments instead) ====================
